import { CompanyData, INDUSTRY_SPECS, StockMarketData } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";

interface Props {
  company: CompanyData;
  market: StockMarketData;
  isSelected: boolean;
  onClick: () => void;
}

export default function CompanyStockCard({ company, market, isSelected, onClick }: Props) {
  const { isDark } = useTheme();
  const spec = INDUSTRY_SPECS[company.industry];

  const isUp = company.stockPrice >= market.prevClose;
  const changeAmt = company.stockPrice - market.prevClose;
  const changePct = market.prevClose > 0 ? (changeAmt / market.prevClose) * 100 : 0;

  const cardBg = isDark
    ? isSelected ? "bg-amber-400/10 border-amber-400/40" : "bg-[#0f1015] border-white/8 hover:border-white/20"
    : isSelected ? "bg-amber-50 border-amber-400/40" : "bg-white border-gray-200 hover:border-gray-300";

  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";

  // Mini sparkline
  const recent = market.priceHistory.slice(-24);
  const minP = Math.min(...recent.map((p) => p.price));
  const maxP = Math.max(...recent.map((p) => p.price));
  const range = maxP - minP || 1;
  const W = 80;
  const H = 30;
  const pts = recent.map((p, i) => {
    const x = (i / Math.max(recent.length - 1, 1)) * W;
    const y = H - ((p.price - minP) / range) * H;
    return `${x},${y}`;
  });
  const sparkPath = pts.length > 1 ? `M ${pts.join(" L ")}` : "";

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-2xl border p-4 transition-all cursor-pointer ${cardBg}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
          <i className={`${spec.icon} text-xl ${spec.color}`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className={`text-sm font-bold truncate ${textPrimary}`}>{company.name}</p>
          </div>
          <p className={`text-xs ${textSecondary}`}>{spec.label}</p>
        </div>
        {/* Sparkline */}
        <div className="flex-shrink-0">
          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
            {sparkPath && (
              <path
                d={sparkPath}
                fill="none"
                stroke={isUp ? "#34d399" : "#f87171"}
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            )}
          </svg>
        </div>
      </div>

      <div className="flex items-end justify-between mt-3">
        <div>
          <p className={`text-lg font-black ${textPrimary}`}>
            {company.stockPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className={`text-xs ${textSecondary}`}>coins/株</p>
        </div>
        <div className="text-right">
          <p className={`text-sm font-bold ${isUp ? "text-emerald-400" : "text-red-400"}`}>
            {isUp ? "+" : ""}{changePct.toFixed(2)}%
          </p>
          <p className={`text-xs ${textSecondary}`}>出来高: {market.dayVolume.toLocaleString()}</p>
        </div>
      </div>
    </button>
  );
}
